/* Pocket Factory: the free tools' QR code engine (M36; the QR code generator and the review link generator). Static codes
   drawn in the browser with the theme's own QR library (assets/qr-code-generator.js, the gift card's), loaded with import()
   the first time a code is drawn: nothing is sent anywhere, nothing is tracked, and a code never expires. Error correction
   M, with the 4-module quiet zone a scanner needs around it.
   window.PocketQR:
   - matrix(text) → Promise<{ count, dark(row, col) } | null>: the code's modules
   - png(text, { scale = 24 }) → Promise<Blob | null>: a PNG, scale pixels a module (null without a canvas)
   - svg(text) → Promise<string | null>: an SVG, one path of dark modules, sharp at any print size
   - use(library): this QR library instead of the one imported (a page that already has it, or a test)
   Loaded once per page, before the tool that uses it:
   <script src="{{ 'pf-qr.js' | asset_url }}" data-qr-src="{{ 'qr-code-generator.js' | asset_url }}" defer></script> */
(function (root) {
  'use strict';
  if (typeof document === 'undefined' || root.PocketQR) return;
  const QUIET = 4;
  // QRErrorCorrectLevel.M in the library.
  const LEVEL_M = 0;
  const src = document.currentScript?.dataset.qrSrc || '';
  let library = null;
  function load() {
    if (!library) {
      try { library = src ? Promise.resolve(import(src)).catch(() => null) : Promise.resolve(null); } catch (_) { library = Promise.resolve(null); }
    }
    return library;
  }

  async function matrix(text) {
    const lib = await load();
    if (!text || typeof lib?.QRCode !== 'function') return null;
    try {
      // The library draws a small SVG of its own into a detached node; its model (_oQRCode in this theme's copy) is the
      // code, drawn again here at the size each format needs.
      const model = new lib.QRCode(document.createElement('div'), { text, width: 64, height: 64, correctLevel: LEVEL_M, useSVG: true })._oQRCode;
      const count = model.getModuleCount();
      return count > 0 ? { count, dark: (row, col) => model.isDark(row, col) } : null;
    } catch (_) {
      return null;
    }
  }

  async function png(text, { scale = 24 } = {}) {
    const code = await matrix(text);
    if (!code) return null;
    try {
      const size = (code.count + QUIET * 2) * scale, canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const context = canvas.getContext('2d');
      if (!context) return null;
      context.fillStyle = '#fff';
      context.fillRect(0, 0, size, size);
      context.fillStyle = '#000';
      for (let row = 0; row < code.count; row++) for (let col = 0; col < code.count; col++) if (code.dark(row, col)) context.fillRect((col + QUIET) * scale, (row + QUIET) * scale, scale, scale);
      return await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
    } catch (_) {
      return null;
    }
  }

  async function svg(text) {
    const code = await matrix(text);
    if (!code) return null;
    const size = code.count + QUIET * 2;
    // One path: each row's runs of dark modules.
    let d = '';
    for (let row = 0; row < code.count; row++) {
      for (let col = 0; col < code.count; col++) {
        if (!code.dark(row, col)) continue;
        let run = 1;
        while (col + run < code.count && code.dark(row, col + run)) run++;
        d += `M${col + QUIET} ${row + QUIET}h${run}v1h-${run}z`;
        col += run - 1;
      }
    }
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${size} ${size}" width="${size * 8}" height="${size * 8}" shape-rendering="crispEdges"><rect width="${size}" height="${size}" fill="#fff"/><path d="${d}" fill="#000"/></svg>`;
  }

  function use(lib) {
    library = Promise.resolve(lib || null);
  }

  root.PocketQR = { matrix, png, svg, use, QUIET };
})(typeof window !== 'undefined' ? window : this);
